import { computed, Injectable, signal, type Signal } from "@angular/core";
import { FieldTree, submit } from "@angular/forms/signals";
import type { IStepperStep } from "@front/m19-ui";

export type FormShellContentSize = "sm" | "md" | "full";

export type IFormStep<T> = keyof T;

export interface IFormNavigationStep<T> {
  stepId: IFormStep<T>;
  label: string;
  size?: FormShellContentSize;
  showWhen?: Signal<boolean>;
}

export interface FormNavigationConfig<T> {
  form: FieldTree<T>;
  blocked?: Signal<boolean>;
  steps: IFormNavigationStep<T>[];
}

@Injectable()
export class FormNavigationService<T> {
  private readonly selectedStepId = signal<IFormStep<T>>(null!);
  private readonly reachedStepIds = signal<ReadonlySet<IFormStep<T>>>(new Set());
  private readonly visibleSteps = computed(() => {
    return this.config.steps.filter((step) => step.showWhen?.() ?? true);
  });
  private readonly visibleStepIds = computed(() => this.visibleSteps().map((step) => step.stepId));

  private config!: FormNavigationConfig<T>;

  readonly currentStepId = computed<IFormStep<T>>(() => {
    const visibleStepsIds = this.visibleStepIds();
    const selected = this.selectedStepId();
    if (visibleStepsIds.includes(selected)) return selected;
    return visibleStepsIds[0] ?? this.config.steps[0].stepId;
  });

  readonly currentStepSize = computed(() => {
    const current = this.currentStepId();
    const step = this.config.steps.find((s) => s.stepId === current);
    return step?.size ?? "sm";
  });

  private readonly effectiveReachedStepIds = computed(() => {
    const visible = this.visibleStepIds();
    const visibleSet = new Set(visible);
    const reached = new Set([...this.reachedStepIds()].filter((id) => visibleSet.has(id)));
    reached.add(this.currentStepId());
    return reached;
  });

  readonly formSteps = computed((): IStepperStep<IFormStep<T>>[] => {
    const current = this.currentStepId();
    const reached = this.effectiveReachedStepIds();

    return this.visibleSteps().map((step) => ({
      id: step.stepId,
      label: step.label,
      state: step.stepId === current ? "current" : reached.has(step.stepId) ? "default" : "disabled",
      controls: [String(step.stepId)],
    }));
  });

  readonly isFirstStep = computed(() => this.visibleSteps()[0].stepId === this.currentStepId());
  readonly isLastStep = computed(() => {
    const visible = this.visibleSteps();
    return visible[visible.length - 1].stepId === this.currentStepId();
  });

  readonly canGoNext = computed(() => {
    if (this.config.blocked?.()) return false;
    return this.isCurrentStepValid();
  });

  configure(config: FormNavigationConfig<T>): void {
    this.config = config;
    const firstVisibleStep = this.visibleSteps()[0];
    this.selectedStepId.set(firstVisibleStep.stepId);
    this.reachedStepIds.set(new Set([firstVisibleStep.stepId]));
  }

  updateReachedStepIds(stepIds: IFormStep<T>[]): void {
    this.reachedStepIds.set(new Set(stepIds));
    if (stepIds.length > 0 && !stepIds.includes(this.selectedStepId())) {
      this.selectedStepId.set(stepIds[stepIds.length - 1]!);
    }
  }

  goToStep(stepId: IFormStep<T>): void {
    if (this.config.blocked?.()) return;
    if (!this.effectiveReachedStepIds().has(stepId)) return;
    this.selectedStepId.set(stepId);
  }

  back(): void {
    if (this.config.blocked?.()) return;
    const visible = this.visibleStepIds();
    const index = visible.indexOf(this.currentStepId());
    if (index > 0) {
      this.selectedStepId.set(visible[index - 1]!);
    }
  }

  next(): void {
    if (!this.canGoNext()) return;
    if (this.isLastStep()) {
      void submit(this.config.form);
    }
    const visible = this.visibleStepIds();
    const index = visible.indexOf(this.currentStepId());
    if (index >= 0 && index < visible.length - 1) {
      this.markReachedAndGo(visible[index + 1]!);
    }
  }

  private isCurrentStepValid(): boolean {
    const stepFields = this.config.form as Partial<Record<IFormStep<T>, FieldTree<unknown>>>;
    return stepFields[this.currentStepId()]?.().valid() ?? true;
  }

  private markReachedAndGo(stepId: IFormStep<T>): void {
    this.reachedStepIds.update((prev) => {
      if (prev.has(stepId)) return prev;
      const next = new Set(prev);
      next.add(stepId);
      return next;
    });
    this.selectedStepId.set(stepId);
  }
}
